/* eslint-disable jsx-a11y/label-has-associated-control */
import Button from 'components/Globals/Base/Button';
import Link from 'components/Globals/Base/Link';
import MyAccount from 'components/MyAccount';
import { Logo } from 'icons/Logo';
import { UserContext } from 'providers/UserProvider';
import { useContext, useRef } from 'react';
import showLogin from 'services/Piano/showLogin';
import { trackEvent } from 'services/Gtm/functions';
import { Microsites, sourceSubscribeCTA } from 'constants/constants';
import Placeholder from 'components/Placeholder';
import { Bars } from 'icons/Bars';
import { DrawerToggleButton } from 'components/Header/HeaderStyles';
import { useLongPress } from '@/app_lib/hooks/useLongPress';
import { FortuneReleaseModal, type FortuneReleaseModalRef } from '../FortuneReleaseModal';
import * as S from './DesktopNavigationBarStyles';
import { trackOptimizelyEvent } from '@/main/lib/providers/OptimizelyProvider';
import { FortuneReleaseBadge } from '@/app_components/release-tools/FortuneReleaseBadge';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

export interface NavigationBarProps {
  domainLogo: string | null;
  subDomain: string;
  logoHref: string;
}
// eslint-disable-next-line react/no-danger, max-len
// biome-ignore lint/security/noDangerouslySetInnerHtml: The code is needed to render svgs
const renderDomainLogo = (html?: string | null) => html && <div dangerouslySetInnerHTML={{ __html: html }} />;

export const showInstreamAd = (micrositeDomain: string | undefined) => {
  googletag
    .defineSlot(
      `/21809533738/${Microsites.Well === micrositeDomain ? 'fortune.well' : 'fortune'}/search/advancedsearch/instream`,
      [
        [728, 90],
        [300, 250],
        [336, 280],
      ],
      'InstantSearch_InStream0',
    )
    ?.addService(googletag.pubads());
  googletag.pubads().refresh();
  document.getElementById('queryly_query')?.addEventListener('keydown', () => {
    googletag.pubads().refresh();
  });
};

const DesktopNavigationBar = (props: NavigationBarProps) => {
  const { domainLogo, subDomain, logoHref } = props;
  const { userState: user, pianoScriptFailed } = useContext(UserContext);
  const { userFlags: userVariants } = useOptimizely();
  const hideHamburgerMenu =
    (
      userVariants?.hideHamburgerMenu_desktop as {
        variant: string;
      }
    )?.variant === 'on';

  const releaseModalRef = useRef<FortuneReleaseModalRef>(null);

  const longPressHandlers = useLongPress({
    duration: 500,
    onLongPress: () => {
      releaseModalRef.current?.openModal();
    },
  });

  const shouldRenderMyAccount = true;

  const displayLogo = ['well'].includes(subDomain);
  const redirect = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';

  const trackingData = {
    contentPlacementCD: 'search hero',
    eventAction: 'search hero experiment',
    eventCategory: 'search',
  };
  const burgerTrackingData = {
    contentPlacementCD: 'Global Side navigation',
    eventAction: 'Global Side navigation toggle',
    eventCategory: 'navigation',
    eventLabel: 'open',
  };

  const trackClick = (event: string) => {
    trackEvent({ ...trackingData, eventLabel: event });
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  const isWellMigrated = process.env.FEATURE_FLAG_WELL_MIGRATION === 'true';

  return (
    <S.NavigationContent
      className={subDomain}
      key='desktop'
      isWellMigrated={isWellMigrated}
    >
      <S.NavigationLeft>
        {!hideHamburgerMenu && (
          <DrawerToggleButton
            htmlFor='drawer-toggle'
            className='hamburger-toggle'
            onClick={() => {
              if (trackEvent && burgerTrackingData) trackEvent(burgerTrackingData);
            }}
            onKeyDown={handleKeyDown}
            aria-label='Open menu'
            data-cy='burger-button'
            tabIndex={0}
            {...longPressHandlers}
          >
            <Bars
              onClick={() => trackOptimizelyEvent('navigation_hamburger_menu_click', {})}
              isMobile={false}
            />
          </DrawerToggleButton>
        )}
        <S.NavigationSearchWrapper
          className={subDomain}
          tabIndex={0}
          onClick={() => {
            showInstreamAd(subDomain);
            trackClick('Search');
          }}
          onKeyDown={() => trackClick('Search')}
          data-cy='searchButton'
        >
          <label htmlFor='queryly_toggle'>Search</label>
        </S.NavigationSearchWrapper>
      </S.NavigationLeft>
      <S.NavigationCenter className={subDomain}>
        <Link
          href={logoHref}
          ariaLabel='Homepage'
          dataCy='logo'
        >
          {(displayLogo && renderDomainLogo(domainLogo)) || <Logo />}
          <FortuneReleaseBadge />
        </Link>
      </S.NavigationCenter>
      {shouldRenderMyAccount && (
        <S.NavigationRight className={subDomain}>
          {user.isLoggedIn === null && user.hasAccess === null && pianoScriptFailed === false && (
            <Placeholder
              size={{ height: 40, width: 200 }}
              className='account-placeholder'
            />
          )}

          {!(user.hasAccess || user.isPaused) && (
            <Link
              className='subscribe-link'
              href={`/subscribe/?itm_source=${sourceSubscribeCTA}&redirect=${encodeURIComponent(redirect)}`}
              dataCy='subscribeBtnDesktop'
              type='accentButton'
              size={{ default: 'xsmall' }}
              hasRightArrow={false}
            >
              Subscribe Now
            </Link>
          )}
          {user.isLoggedIn === false && (
            <Button
              className='sign-in-button'
              onClick={showLogin}
              dataCy='signinBtnDesktop'
              variant='tertiary'
              hasRightArrow={false}
            >
              Sign In
            </Button>
          )}
          {user.isLoggedIn && <MyAccount className={`${subDomain} account-menu`} />}
        </S.NavigationRight>
      )}
      <FortuneReleaseModal ref={releaseModalRef} />
    </S.NavigationContent>
  );
};

export default DesktopNavigationBar;
